import type { Expression, IfStatement, LValue, Statement, SwitchStatement } from '../parser/ast.js';
import {
  asSimpleSequenceCandidate,
  availableSingleNodeConversions,
  classifyIf,
  collectAllNames,
  findNestedSimpleChains,
  findSimpleChains,
  multipleToSwitch,
  renameEligibleSimpleSequence,
} from './selection.js';
import type { SelectionShape } from './selection.js';

/**
 * Περιδιαβαίνει ΟΛΟΚΛΗΡΟ το πρόγραμμα (όχι μόνο την επιφάνεια — και μέσα σε
 * ΓΙΑ/ΟΣΟ/ΑΡΧΗ_ΕΠΑΝΑΛΗΨΗΣ, και μέσα σε ΑΝ/ΕΠΙΛΕΞΕ) για να βρει κάθε δομή
 * επιλογής με τουλάχιστον μία έγκυρη μετατροπή — αυτό ζήτησε ο χρήστης ως
 * "αυτόματος εντοπισμός" αντί να διαλέγει ο ίδιος ποιο κομμάτι κώδικα.
 */

export interface SingleNodeOption {
  readonly targetShape: SelectionShape;
  /** Εφαρμόζει τη μετατροπή πάνω στο ΠΛΗΡΕΣ σύνολο εντολών (root) και επιστρέφει το νέο πλήρες σύνολο — όχι μόνο την τοπική αντικατάσταση, αφού η αυτόματη μετονομασία (βλ. `renameEligibleSimpleSequence`) χρειάζεται να ξαναγράψει και ΕΠΟΜΕΝΕΣ εντολές έξω από τον κόμβο. */
  readonly apply: (root: readonly Statement[]) => readonly Statement[];
}

export interface SingleNodeDetection {
  readonly kind: 'single';
  readonly node: IfStatement | SwitchStatement;
  readonly options: readonly SingleNodeOption[];
}

export interface ChainOption {
  readonly targetShape: SelectionShape;
  readonly apply: () => IfStatement | SwitchStatement;
}

export interface ChainDetection {
  readonly kind: 'chain';
  /** Οι διαδοχικές Απλές ΑΝ (2 ή περισσότερες) που θα ενωθούν σε μία Σύνθετη/Πολλαπλή. */
  readonly statements: readonly IfStatement[];
  /**
   * Πάντα τουλάχιστον 1 επιλογή (η ίδια η ένωση, Σύνθετη/Πολλαπλή) — ΚΑΙ,
   * όποτε το ενωμένο αποτέλεσμα δέχεται ΚΑΙ μετατροπή σε ΕΠΙΛΕΞΕ (βλ.
   * `multipleToSwitch`), μια 2η επιλογή που συνδυάζει και τα δύο βήματα σε
   * ένα κλικ — έτσι ο χρήστης δεν χρειάζεται να ξαναδώσει το αποτέλεσμα σαν
   * νέα είσοδο για να δει το ΕΠΙΛΕΞΕ.
   */
  readonly options: readonly ChainOption[];
}

export type SelectionDetection = SingleNodeDetection | ChainDetection;

const EMPTY_SET: ReadonlySet<Statement> = new Set();

export function detectSelectionStructures(statements: readonly Statement[]): SelectionDetection[] {
  const items: SelectionDetection[] = [];
  // Όλα τα ονόματα που ήδη χρησιμοποιούνται ΟΠΟΥΔΗΠΟΤΕ στο πρόγραμμα — ώστε
  // η αυτόματη μετονομασία (βλ. παρακάτω) να διαλέγει πάντα ένα φρέσκο,
  // χωρίς συγκρούσεις όνομα.
  const allNames = collectAllNames(statements);

  function wrapLocalOptions(node: IfStatement | SwitchStatement, options: readonly { readonly targetShape: SelectionShape; readonly apply: () => readonly Statement[] }[]): SingleNodeOption[] {
    return options.map((o) => ({ targetShape: o.targetShape, apply: (root: readonly Statement[]) => replaceNode(root, node, o.apply()) }));
  }

  /** Προσπαθεί το "→ Απλή" μέσω αυτόματης μετονομασίας, ΜΟΝΟ όταν η απλή (χωρίς μετονομασία) εκδοχή δεν είναι ήδη διαθέσιμη. */
  function tryRenameSimpleOption(node: IfStatement | SwitchStatement): SingleNodeOption | null {
    const candidate = asSimpleSequenceCandidate(node);
    if (candidate === null) return null;
    const result = renameEligibleSimpleSequence(candidate, allNames);
    if (result === null) return null;
    return {
      targetShape: 'simple',
      apply: (root) => applyRenameSimpleReplacement(root, node, result.statements, result.renames),
    };
  }

  // `insideBranch` είναι true όταν η τρέχουσα λίστα εντολών είναι κλάδος
  // (ΤΟΤΕ/ΑΛΛΙΩΣ_ΑΝ/ΑΛΛΙΩΣ/ΠΕΡΙΠΤΩΣΗ) μιας ΗΔΗ γονικής δομής επιλογής — όχι
  // απλώς μέσα σε βρόχο. Οποιαδήποτε μετατροπή γίνει ΕΚΕΙ ΜΕΣΑ (είτε "→ Απλή"
  // ενός κόμβου, είτε συγχώνευση διαδοχικών Απλών ΑΝ σε Σύνθετη/Πολλαπλή)
  // παραμένει ΠΑΝΤΑ φωλιασμένη μέσα στον κλάδο του γονέα — άρα δεν έχει νόημα
  // να προσφέρεται ως "λύση" (βλ. bug reports: εσωτερική Σύνθετη → Απλή μέσα
  // σε ΑΛΛΙΩΣ έδινε δύο ΑΝ ξανά φωλιασμένες· συγχώνευση δύο εσωτερικών Απλών
  // ΑΝ μέσα σε ΤΟΤΕ έδινε μία Σύνθετη ξανά φωλιασμένη). Η πλήρης εξομάλυνση
  // προσφέρεται ήδη από τον ΕΞΩΤΕΡΙΚΟ κόμβο όποτε υπάρχει έγκυρη (π.χ.
  // nested-else → simple περνάει πρώτα από nestedElseToMultiple)· όποτε δεν
  // υπάρχει έγκυρη εξωτερική μετατροπή, απλά δεν προσφέρεται τίποτα.
  function visitList(list: readonly Statement[], insideBranch: boolean): void {
    let chainMembers: ReadonlySet<Statement> = EMPTY_SET;
    if (!insideBranch) {
      // Δύο ανεξάρτητοι ανιχνευτές αλυσίδας (`findSimpleChains`: ρητή άρνηση·
      // `findNestedSimpleChains`: εμφωλευμένες/υποσύνολο συνθήκες, βλ. εκεί)
      // μπορεί να διεκδικήσουν ΕΠΙΚΑΛΥΠΤΟΜΕΝΕΣ εντολές (π.χ. "x<=5"/"x<=10"/
      // "x>10": η 2η+3η ταιριάζουν ΚΑΙ στο ρητή-άρνηση μοτίβο ΜΟΝΕΣ τους, ΑΛΛΑ
      // και οι 3 μαζί ταιριάζουν στο εμφωλευμένο μοτίβο) — κρατάμε ΠΑΝΤΑ τη
      // ΜΕΓΑΛΥΤΕΡΗ (πιο πλήρη) αλυσίδα όποτε συγκρούονται, απορρίπτοντας τις
      // μικρότερες που μοιράζονται έστω μία εντολή μαζί της.
      const candidates = [...findSimpleChains(list), ...findNestedSimpleChains(list)].sort((a, b) => b.statements.length - a.statements.length);
      const claimed = new Set<Statement>();
      const allChains: typeof candidates = [];
      for (const candidate of candidates) {
        if (candidate.statements.some((s) => claimed.has(s))) continue;
        allChains.push(candidate);
        for (const s of candidate.statements) claimed.add(s);
      }
      if (allChains.length > 0) {
        for (const chain of allChains) {
          const merged = chain.apply();
          const mergedShape = classifyIf(merged);
          const options: ChainOption[] = [{ targetShape: mergedShape, apply: () => merged }];
          // "→ ΕΠΙΛΕΞΕ" μόνο όταν το ενωμένο αποτέλεσμα είναι ΟΝΤΩΣ Πολλαπλή
          // (elseif-αλυσίδα, 3+ κλάδοι) — ίδιος περιορισμός με το
          // `availableSingleNodeConversions` για μεμονωμένο κόμβο· για απλό
          // Σύνθετη (2 κλάδοι) η μετατροπή θα ήταν τετριμμένη/άσκοπη.
          if (mergedShape === 'multiple') {
            const asSwitch = multipleToSwitch(merged);
            if (asSwitch !== null) options.push({ targetShape: 'switch', apply: () => asSwitch });
          }
          items.push({ kind: 'chain', statements: chain.statements, options });
        }
        chainMembers = claimed;
      }
    }
    for (const stmt of list) visitStatement(stmt, insideBranch, chainMembers.has(stmt));
  }

  function visitStatement(stmt: Statement, insideBranch: boolean, isChainMember: boolean): void {
    switch (stmt.kind) {
      case 'IfStatement': {
        // Ένα μέλος μιας ήδη εντοπισμένης αλυσίδας διαδοχικών Απλών ΑΝ (βλ.
        // `findSimpleChains`) δεν πρέπει να προσφέρει ΚΑΙ τη δική του
        // μεμονωμένη μετατροπή (π.χ. "→ Εμφωλευμένη", όταν η συνθήκη του
        // τυχαίνει να είναι ΚΑΙ) — αυτό θα σήμαινε μετασχηματισμό ΜΟΝΟ ενός
        // μέλους της αλυσίδας, αγνοώντας τα υπόλοιπα, κάτι που ο χρήστης
        // ρητά δεν θέλει: για διαδοχικές Απλές, η μόνη προσφερόμενη
        // μετατροπή πρέπει να είναι η συγχώνευση σε Σύνθετη/Πολλαπλή.
        if (!isChainMember) {
          let localOptions = availableSingleNodeConversions(stmt);
          if (insideBranch) localOptions = localOptions.filter((o) => o.targetShape !== 'simple');
          const options = wrapLocalOptions(stmt, localOptions);
          if (!insideBranch && !localOptions.some((o) => o.targetShape === 'simple')) {
            const renameOption = tryRenameSimpleOption(stmt);
            if (renameOption !== null) options.push(renameOption);
          }
          if (options.length > 0) items.push({ kind: 'single', node: stmt, options });
        }
        visitList(stmt.thenBranch, true);
        for (const branch of stmt.elseIfBranches) visitList(branch.body, true);
        if (stmt.elseBranch !== null) visitList(stmt.elseBranch, true);
        return;
      }
      case 'SwitchStatement': {
        let localOptions = availableSingleNodeConversions(stmt);
        if (insideBranch) localOptions = localOptions.filter((o) => o.targetShape !== 'simple');
        const options = wrapLocalOptions(stmt, localOptions);
        if (!insideBranch && !localOptions.some((o) => o.targetShape === 'simple')) {
          const renameOption = tryRenameSimpleOption(stmt);
          if (renameOption !== null) options.push(renameOption);
        }
        if (options.length > 0) items.push({ kind: 'single', node: stmt, options });
        for (const c of stmt.cases) visitList(c.body, true);
        if (stmt.elseBody !== null) visitList(stmt.elseBody, true);
        return;
      }
      case 'ForStatement':
      case 'WhileStatement':
      case 'RepeatUntilStatement':
        visitList(stmt.body, false);
        return;
      default:
        return;
    }
  }

  visitList(statements, false);
  return items;
}

// --- Αντικατάσταση ενός εντοπισμένου κόμβου/αλυσίδας μέσα στο ΣΥΝΟΛΙΚΟ σώμα -

/**
 * Ελέγχει (με βάση ταυτότητα αναφοράς) αν το `target` βρίσκεται οπουδήποτε
 * μέσα στο υποδέντρο του `stmt` — χρησιμοποιείται ΩΣ ΕΠΙ ΤΟ ΠΛΕΙΣΤΟΝ για να
 * ΑΠΟΦΥΓΟΥΜΕ να ξαναχτίσουμε (άρα να αλλάξουμε ταυτότητα αναφοράς) κλάδους
 * που δεν έχουν καμία σχέση με το target· αυτό επιτρέπει να συνθέτουμε
 * πολλαπλές, ανεξάρτητες `apply` κλήσεις διαδοχικά (βλ. στο main.ts τον
 * συνδυασμό πολλών ταυτόχρονα επιλεγμένων μετατροπών) χωρίς η μία να
 * "χαλάσει" την αναφορά που κρατάει η άλλη.
 */
function containsNode(stmt: Statement, target: Statement): boolean {
  if (stmt === target) return true;
  switch (stmt.kind) {
    case 'IfStatement':
      return (
        stmt.thenBranch.some((s) => containsNode(s, target)) ||
        stmt.elseIfBranches.some((b) => b.body.some((s) => containsNode(s, target))) ||
        (stmt.elseBranch !== null && stmt.elseBranch.some((s) => containsNode(s, target)))
      );
    case 'SwitchStatement':
      return (
        stmt.cases.some((c) => c.body.some((s) => containsNode(s, target))) ||
        (stmt.elseBody !== null && stmt.elseBody.some((s) => containsNode(s, target)))
      );
    case 'ForStatement':
    case 'WhileStatement':
    case 'RepeatUntilStatement':
      return stmt.body.some((s) => containsNode(s, target));
    default:
      return false;
  }
}

function rebuildWithReplacement(stmt: Statement, target: Statement, replacement: readonly Statement[]): Statement {
  if (!containsNode(stmt, target)) return stmt;
  switch (stmt.kind) {
    case 'IfStatement':
      return {
        ...stmt,
        thenBranch: replaceNode(stmt.thenBranch, target, replacement),
        elseIfBranches: stmt.elseIfBranches.map((b) => ({ ...b, body: replaceNode(b.body, target, replacement) })),
        elseBranch: stmt.elseBranch === null ? null : replaceNode(stmt.elseBranch, target, replacement),
      };
    case 'SwitchStatement':
      return {
        ...stmt,
        cases: stmt.cases.map((c) => ({ ...c, body: replaceNode(c.body, target, replacement) })),
        elseBody: stmt.elseBody === null ? null : replaceNode(stmt.elseBody, target, replacement),
      };
    case 'ForStatement':
    case 'WhileStatement':
    case 'RepeatUntilStatement':
      return { ...stmt, body: replaceNode(stmt.body, target, replacement) };
    default:
      return stmt;
  }
}

/** Αντικαθιστά το `target` (με βάση ταυτότητα αναφοράς, όχι δομική ισότητα) με τη λίστα `replacement`, οπουδήποτε κι αν είναι φωλιασμένο μέσα στο δέντρο. Κλάδοι που δεν περιέχουν το `target` επιστρέφονται ΑΝΑΛΛΟΙΩΤΟΙ (ίδια αναφορά) — βλ. `containsNode`. */
export function replaceNode(statements: readonly Statement[], target: Statement, replacement: readonly Statement[]): Statement[] {
  const result: Statement[] = [];
  for (const stmt of statements) {
    if (stmt === target) {
      result.push(...replacement);
      continue;
    }
    result.push(rebuildWithReplacement(stmt, target, replacement));
  }
  return result;
}

function matchesChainAt(statements: readonly Statement[], startIndex: number, chain: readonly Statement[]): boolean {
  if (startIndex + chain.length > statements.length) return false;
  for (let k = 0; k < chain.length; k++) {
    if (statements[startIndex + k] !== chain[k]) return false;
  }
  return true;
}

function rebuildWithChainReplacement(stmt: Statement, chain: readonly Statement[], replacement: Statement): Statement {
  // Όλα τα μέλη μιας αλυσίδας ζουν πάντα στην ΙΔΙΑ λίστα (βλ. `findSimpleChains`),
  // άρα το πρώτο μέλος αρκεί ως αντιπρόσωπος για τον έλεγχο περιεχομένου.
  if (!containsNode(stmt, chain[0]!)) return stmt;
  switch (stmt.kind) {
    case 'IfStatement':
      return {
        ...stmt,
        thenBranch: replaceChain(stmt.thenBranch, chain, replacement),
        elseIfBranches: stmt.elseIfBranches.map((b) => ({ ...b, body: replaceChain(b.body, chain, replacement) })),
        elseBranch: stmt.elseBranch === null ? null : replaceChain(stmt.elseBranch, chain, replacement),
      };
    case 'SwitchStatement':
      return {
        ...stmt,
        cases: stmt.cases.map((c) => ({ ...c, body: replaceChain(c.body, chain, replacement) })),
        elseBody: stmt.elseBody === null ? null : replaceChain(stmt.elseBody, chain, replacement),
      };
    case 'ForStatement':
    case 'WhileStatement':
    case 'RepeatUntilStatement':
      return { ...stmt, body: replaceChain(stmt.body, chain, replacement) };
    default:
      return stmt;
  }
}

/** Ίδιο με `replaceNode`, αλλά για τη Σύνθετη/Πολλαπλή↔Απλή μετατροπή αλυσίδας — αντικαθιστά Ν ΔΙΑΔΟΧΙΚΕΣ εντολές (βλ. `findSimpleChains`) με μία. */
export function replaceChain(statements: readonly Statement[], chain: readonly Statement[], replacement: Statement): Statement[] {
  const result: Statement[] = [];
  let i = 0;
  while (i < statements.length) {
    if (matchesChainAt(statements, i, chain)) {
      result.push(replacement);
      i += chain.length;
      continue;
    }
    result.push(rebuildWithChainReplacement(statements[i]!, chain, replacement));
    i++;
  }
  return result;
}

// --- Αντικατάσταση ΜΕ αυτόματη μετονομασία (βλ. `renameEligibleSimpleSequence`) --

/**
 * "Ενεργή" κατάσταση μετονομασίας κατά τη διάσχιση των εντολών ΜΕΤΑ τον
 * αντικατεστημένο κόμβο: `renameMap` είναι σταθερό (παλιό→νέο όνομα για
 * ΟΛΕΣ τις συγκρούσεις), ενώ `active` είναι ΜΕΤΑΒΛΗΤΟ σύνολο ονομάτων που
 * παραμένουν ακόμα "υπό μετονομασία" — συρρικνώνεται μόλις βρεθεί μια ΝΕΑ
 * ανάθεση στο αρχικό όνομα (βλ. `renamePropagateStatement`).
 */
interface RenameCtx {
  readonly renameMap: ReadonlyMap<string, string>;
  readonly active: Set<string>;
}

function renameNamesInExpression(expr: Expression, ctx: RenameCtx): Expression {
  switch (expr.kind) {
    case 'NumberLiteral':
    case 'StringLiteral':
    case 'BooleanLiteral':
      return expr;
    case 'Identifier':
      return ctx.active.has(expr.name) ? { ...expr, name: ctx.renameMap.get(expr.name)! } : expr;
    case 'ArrayAccess': {
      const array = ctx.active.has(expr.array.name) ? { ...expr.array, name: ctx.renameMap.get(expr.array.name)! } : expr.array;
      return { ...expr, array, indices: expr.indices.map((i) => renameNamesInExpression(i, ctx)) };
    }
    case 'FunctionCall':
      return { ...expr, args: expr.args.map((a) => renameNamesInExpression(a, ctx)) };
    case 'UnaryExpression':
      return { ...expr, operand: renameNamesInExpression(expr.operand, ctx) };
    case 'BinaryExpression':
      return { ...expr, left: renameNamesInExpression(expr.left, ctx), right: renameNamesInExpression(expr.right, ctx) };
  }
}

/** Ένας ΝΕΟΣ στόχος ανάθεσης πάνω στο αρχικό όνομα σημαίνει "από εδώ και πέρα ανεξάρτητη τιμή" — ο ΣΤΟΧΟΣ δεν μετονομάζεται, αλλά το όνομα σταματάει να είναι "ενεργό". Τυχόν δείκτες πίνακα (LValue ArrayAccess) ΔΙΑΒΑΖΟΝΤΑΙ, άρα μετονομάζονται κανονικά. */
function renamePropagateLValueTarget(target: LValue, ctx: RenameCtx): LValue {
  if (target.kind === 'Identifier') {
    ctx.active.delete(target.name);
    return target;
  }
  const array = ctx.active.has(target.array.name) ? { ...target.array, name: ctx.renameMap.get(target.array.name)! } : target.array;
  return { ...target, array, indices: target.indices.map((i) => renameNamesInExpression(i, ctx)) };
}

function renamePropagateList(statements: readonly Statement[], ctx: RenameCtx): Statement[] {
  return statements.map((stmt) => renamePropagateStatement(stmt, ctx));
}

/** Τρέχει `body` (κλάδος/σώμα βρόχου) με ΤΟ ΔΙΚΟ ΤΟΥ αντίγραφο του ενεργού συνόλου (ξεκινώντας από `startActive`) και επιστρέφει το ΝΕΟ σύνολο ενεργών ονομάτων μετά — χρησιμοποιείται για να συνδυαστούν τα αποτελέσματα πολλών εναλλακτικών μονοπατιών (κλάδοι ΑΝ/ΠΕΡΙΠΤΩΣΗ, επαναλήψεις). */
function runBranch(body: readonly Statement[], renameMap: ReadonlyMap<string, string>, startActive: ReadonlySet<string>): { body: Statement[]; active: Set<string> } {
  const ctx: RenameCtx = { renameMap, active: new Set(startActive) };
  const renamed = renamePropagateList(body, ctx);
  return { body: renamed, active: ctx.active };
}

function renamePropagateStatement(stmt: Statement, ctx: RenameCtx): Statement {
  if (ctx.active.size === 0) return stmt;
  switch (stmt.kind) {
    case 'AssignmentStatement':
      return { ...stmt, value: renameNamesInExpression(stmt.value, ctx), target: renamePropagateLValueTarget(stmt.target, ctx) };
    case 'ReadStatement':
      return { ...stmt, targets: stmt.targets.map((t) => renamePropagateLValueTarget(t, ctx)) };
    case 'WriteStatement':
      return { ...stmt, values: stmt.values.map((v) => renameNamesInExpression(v, ctx)) };
    case 'SwapStatement':
      return { ...stmt, left: renamePropagateLValueTarget(stmt.left, ctx), right: renamePropagateLValueTarget(stmt.right, ctx) };
    case 'CallStatement':
      return { ...stmt, args: stmt.args.map((a) => renameNamesInExpression(a, ctx)) };
    case 'IfStatement': {
      const condition = renameNamesInExpression(stmt.condition, ctx);
      const startActive = new Set(ctx.active);
      const branchActiveAfter: ReadonlySet<string>[] = [];

      const then = runBranch(stmt.thenBranch, ctx.renameMap, startActive);
      branchActiveAfter.push(then.active);

      const elseIfBranches = stmt.elseIfBranches.map((b) => {
        // Η συνθήκη ενός ΑΛΛΙΩΣ_ΑΝ εκτελείται ΠΑΝΤΑ με την κατάσταση ΠΡΙΝ το ΑΝ (δεν έχει τρέξει κανένα σώμα ακόμα).
        const bCondition = renameNamesInExpression(b.condition, { renameMap: ctx.renameMap, active: new Set(startActive) });
        const branch = runBranch(b.body, ctx.renameMap, startActive);
        branchActiveAfter.push(branch.active);
        return { condition: bCondition, body: branch.body };
      });

      let elseBranch: readonly Statement[] | null = null;
      if (stmt.elseBranch !== null) {
        const branch = runBranch(stmt.elseBranch, ctx.renameMap, startActive);
        elseBranch = branch.body;
        branchActiveAfter.push(branch.active);
      } else {
        branchActiveAfter.push(startActive); // "καμία συνθήκη δεν αλήθεψε" μονοπάτι — δεν μεταβάλλει τίποτα
      }

      ctx.active.clear();
      for (const name of startActive) if (branchActiveAfter.every((s) => s.has(name))) ctx.active.add(name);
      return { ...stmt, condition, thenBranch: then.body, elseIfBranches, elseBranch };
    }
    case 'SwitchStatement': {
      const discriminant = renameNamesInExpression(stmt.discriminant, ctx);
      const startActive = new Set(ctx.active);
      const branchActiveAfter: ReadonlySet<string>[] = [];

      const cases = stmt.cases.map((c) => {
        const branch = runBranch(c.body, ctx.renameMap, startActive);
        branchActiveAfter.push(branch.active);
        return { ...c, body: branch.body };
      });

      let elseBody: readonly Statement[] | null = null;
      if (stmt.elseBody !== null) {
        const branch = runBranch(stmt.elseBody, ctx.renameMap, startActive);
        elseBody = branch.body;
        branchActiveAfter.push(branch.active);
      } else {
        branchActiveAfter.push(startActive);
      }

      ctx.active.clear();
      for (const name of startActive) if (branchActiveAfter.every((s) => s.has(name))) ctx.active.add(name);
      return { ...stmt, discriminant, cases, elseBody };
    }
    case 'ForStatement': {
      const from = renameNamesInExpression(stmt.from, ctx);
      const to = renameNamesInExpression(stmt.to, ctx);
      const step = stmt.step === null ? null : renameNamesInExpression(stmt.step, ctx);
      const startActive = new Set(ctx.active);
      startActive.delete(stmt.variable.name); // ο μετρητής ξαναγράφεται σε κάθε επανάληψη
      // Ο βρόχος τρέχει 0 ή περισσότερες φορές — ασφαλές μετά είναι ό,τι παραμένει ενεργό ΚΑΙ στις δύο περιπτώσεις.
      const body = runBranch(stmt.body, ctx.renameMap, startActive);
      ctx.active.clear();
      for (const name of startActive) if (body.active.has(name)) ctx.active.add(name);
      return { ...stmt, from, to, step, body: body.body };
    }
    case 'WhileStatement': {
      const condition = renameNamesInExpression(stmt.condition, ctx);
      const startActive = new Set(ctx.active);
      const body = runBranch(stmt.body, ctx.renameMap, startActive);
      ctx.active.clear();
      for (const name of startActive) if (body.active.has(name)) ctx.active.add(name);
      return { ...stmt, condition, body: body.body };
    }
    case 'RepeatUntilStatement': {
      // Εκτελείται ΤΟΥΛΑΧΙΣΤΟΝ μία φορά — δεν υπάρχει "0 επαναλήψεων" μονοπάτι να συνδυαστεί.
      const body = runBranch(stmt.body, ctx.renameMap, ctx.active);
      const bodyCtx: RenameCtx = { renameMap: ctx.renameMap, active: body.active };
      const condition = renameNamesInExpression(stmt.condition, bodyCtx);
      ctx.active.clear();
      for (const name of body.active) ctx.active.add(name);
      return { ...stmt, body: body.body, condition };
    }
  }
}

function findAndReplaceWithRename(
  statements: readonly Statement[],
  target: Statement,
  replacement: readonly Statement[],
  renameMap: ReadonlyMap<string, string>,
): Statement[] {
  const idx = statements.indexOf(target);
  if (idx !== -1) {
    const before = statements.slice(0, idx);
    const after = statements.slice(idx + 1);
    const ctx: RenameCtx = { renameMap, active: new Set(renameMap.keys()) };
    const renamedAfter = renamePropagateList(after, ctx);
    return [...before, ...replacement, ...renamedAfter];
  }
  return statements.map((stmt) => rebuildContainingRename(stmt, target, replacement, renameMap));
}

function rebuildContainingRename(stmt: Statement, target: Statement, replacement: readonly Statement[], renameMap: ReadonlyMap<string, string>): Statement {
  if (!containsNode(stmt, target)) return stmt;
  switch (stmt.kind) {
    case 'IfStatement':
      return {
        ...stmt,
        thenBranch: findAndReplaceWithRename(stmt.thenBranch, target, replacement, renameMap),
        elseIfBranches: stmt.elseIfBranches.map((b) => ({ ...b, body: findAndReplaceWithRename(b.body, target, replacement, renameMap) })),
        elseBranch: stmt.elseBranch === null ? null : findAndReplaceWithRename(stmt.elseBranch, target, replacement, renameMap),
      };
    case 'SwitchStatement':
      return {
        ...stmt,
        cases: stmt.cases.map((c) => ({ ...c, body: findAndReplaceWithRename(c.body, target, replacement, renameMap) })),
        elseBody: stmt.elseBody === null ? null : findAndReplaceWithRename(stmt.elseBody, target, replacement, renameMap),
      };
    case 'ForStatement':
    case 'WhileStatement':
    case 'RepeatUntilStatement':
      return { ...stmt, body: findAndReplaceWithRename(stmt.body, target, replacement, renameMap) };
    default:
      return stmt;
  }
}

/**
 * Σαν το `replaceNode`, αλλά ΕΠΙΠΛΕΟΝ ξαναγράφει (με βάση το `renameMap`,
 * παλιό→νέο όνομα) κάθε ΕΠΟΜΕΝΗ αναφορά στο παλιό όνομα, στην ΙΔΙΑ λίστα
 * εντολών που περιέχει το `target`, ΜΕΧΡΙ το σημείο όπου το παλιό όνομα
 * ξαναπαίρνει τιμή με νέα ανάθεση/ΔΙΑΒΑΣΕ/ΑΝΤΑΛΛΑΓΗ (οπότε αποκτά νέα,
 * ανεξάρτητη τιμή, και η μετονομασία σταματάει από εκεί και πέρα) — βλ.
 * `renameEligibleSimpleSequence` στο selection.ts.
 */
export function applyRenameSimpleReplacement(
  root: readonly Statement[],
  target: Statement,
  replacement: readonly Statement[],
  renameMap: ReadonlyMap<string, string>,
): Statement[] {
  return findAndReplaceWithRename(root, target, replacement, renameMap);
}

// --- Σειρά εφαρμογής ΠΟΛΛΑΠΛΩΝ, ταυτόχρονα επιλεγμένων μετατροπών ---

function visitDepth(stmt: Statement, depth: number, depths: Map<Statement, number>): void {
  depths.set(stmt, depth);
  switch (stmt.kind) {
    case 'IfStatement':
      for (const s of stmt.thenBranch) visitDepth(s, depth + 1, depths);
      for (const b of stmt.elseIfBranches) for (const s of b.body) visitDepth(s, depth + 1, depths);
      if (stmt.elseBranch !== null) for (const s of stmt.elseBranch) visitDepth(s, depth + 1, depths);
      return;
    case 'SwitchStatement':
      for (const c of stmt.cases) for (const s of c.body) visitDepth(s, depth + 1, depths);
      if (stmt.elseBody !== null) for (const s of stmt.elseBody) visitDepth(s, depth + 1, depths);
      return;
    case 'ForStatement':
    case 'WhileStatement':
    case 'RepeatUntilStatement':
      for (const s of stmt.body) visitDepth(s, depth + 1, depths);
      return;
    default:
      return;
  }
}

/**
 * Υπολογίζει το βάθος εμφώλευσης (0 = επιφάνεια) κάθε εντολής στο δέντρο, με
 * βάση ταυτότητα αναφοράς — χρησιμοποιείται στο main.ts για να εφαρμόσει με
 * ΑΣΦΑΛΗ σειρά πολλαπλές, ταυτόχρονα επιλεγμένες μετατροπές: όποτε μία στοχεύει
 * κόμβο που είναι ΠΡΟΓΟΝΟΣ μιας άλλης, η μετατροπή του προγόνου ΠΡΕΠΕΙ να
 * εφαρμοστεί πρώτη (μικρότερο βάθος πρώτα) — αλλιώς η αναδόμηση του προγόνου
 * θα άλλαζε ταυτότητα αναφοράς πριν προλάβει να εφαρμοστεί η δική του
 * μετατροπή. Για ασύνδετους (αδέρφια/ξαδέρφια) στόχους η σειρά δεν έχει
 * σημασία, αφού το `replaceNode`/`replaceChain` πλέον αφήνει αναλλοίωτους
 * τους κλάδους που δεν περιέχουν τον στόχο (βλ. `containsNode`).
 */
export function computeNodeDepths(statements: readonly Statement[]): Map<Statement, number> {
  const depths = new Map<Statement, number>();
  for (const stmt of statements) visitDepth(stmt, 0, depths);
  return depths;
}
